// Copyright (c) 2026 Michael F. Kamprath
//
// quit-button.js -- red-X "return to home" button overlay.
//
// Rendered as an HTML <button> element layered above the PixiJS canvas
// (mirroring the home-screen gear button in spirit, but lives at the
// app root so it can stay visible across scene rebuilds).  The graphic
// is the legacy quit_game_button_normal@2x.png from the Obj-C app
// (48x48 source PNG), scaled responsively via CSS clamp() so the touch
// target stays comfortable on phones, tablets, and desktop.
//
// Styling is aspect-ratio aware: callers pass a per-variant config via
// setConfig({ standard: {...}, wide: {...} }) containing imageUrl,
// size, and a center point (top/right offsets of the button's
// centroid).  The module re-applies the active variant's styling on
// every window resize so crossing the standard/wide threshold swaps
// the look without a scene rebuild.  When no config is set, the
// module leaves the baseline styling from quit-button.css untouched
// (and uses the imageUrl from createQuitButton's `imageUrl` option,
// if any).
//
// Lifecycle:
//   - createQuitButton(parent, { onQuit, getVariant, config }) builds
//     the element, attaches it to the parent, wires a resize listener,
//     and returns control handles.
//   - The button starts hidden -- callers call show() once a scene is
//     up and hide() when the home screen is brought back.
//   - Click invokes onQuit; the callback is responsible for tearing
//     down the active scene + showing the home screen.

const QUIT_BUTTON_IMAGE_URL = '/images/ui/quit_game_button.png';

/**
 * @typedef {{ imageUrl: string, size: string, center: { top: string, right: string } }} QuitButtonVariantStyle
 */

/**
 * @typedef {{ standard?: QuitButtonVariantStyle, wide?: QuitButtonVariantStyle }} QuitButtonConfig
 */

/**
 * Default getVariant -- picks 'wide' when the viewport aspect ratio
 * sits above the midpoint between 4:3 and 16:9 (~1.555), matching the
 * app's canvas.getLayoutVariant() heuristic.  Falls back to 'standard'
 * if window dimensions are unavailable so it's safe in tests without
 * a configured DOM.
 */
function defaultGetVariant() {
  if (typeof window === 'undefined') return 'standard';
  const w = window.innerWidth;
  const h = window.innerHeight;
  if (!Number.isFinite(w) || !Number.isFinite(h) || h <= 0) return 'standard';
  return (w / h) >= 1.5555 ? 'wide' : 'standard';
}

/**
 * Create the quit button and attach it to the parent.
 *
 * @param {HTMLElement} parent
 * @param {{
 *   onQuit?: () => void,
 *   imageUrl?: string,
 *   config?: QuitButtonConfig,
 *   getVariant?: () => 'standard' | 'wide',
 * }} [opts]
 * @returns {{
 *   element: HTMLButtonElement,
 *   show: () => void,
 *   hide: () => void,
 *   setConfig: (cfg: QuitButtonConfig | null) => void,
 *   destroy: () => void,
 * }}
 */
export function createQuitButton(parent, opts = {}) {
  const onQuit = typeof opts.onQuit === 'function' ? opts.onQuit : () => {};
  const imageUrl = opts.imageUrl ?? QUIT_BUTTON_IMAGE_URL;
  /** @type {QuitButtonConfig | null} */
  let config = opts.config || null;
  const getVariant = typeof opts.getVariant === 'function'
    ? opts.getVariant
    : defaultGetVariant;
  /** @type {'standard' | 'wide' | null} */
  let currentVariant = null;

  const btn = document.createElement('button');
  btn.type = 'button';
  btn.className = 'quit-button';
  btn.setAttribute('aria-label', 'Return to game selection');
  // Source PNG is 48x48 (legacy iOS @2x asset, decoded as red X on
  // transparent background).  We render it as a CSS background so the
  // CSS can size + scale the button independent of the image's pixel
  // dimensions.
  btn.style.backgroundImage = `url(${imageUrl})`;
  // Hidden by default -- only visible while a scene is running.
  btn.style.display = 'none';

  /**
   * Apply the currently active variant's styling to the element.  Pure
   * function of `config` + `getVariant()`; touches only the button's
   * inline style.  When no config is set, leaves the CSS baseline
   * (quit-button.css) alone so the legacy defaults still apply.
   */
  function applyVariantStyles() {
    if (!config) return;
    const variant = getVariant();
    currentVariant = variant;
    const v = config[variant] || config.standard || config.wide;
    if (!v) return;
    if (v.imageUrl) {
      btn.style.backgroundImage = `url(${v.imageUrl})`;
    }
    if (v.size) {
      btn.style.width = v.size;
      btn.style.height = v.size;
    }
    if (v.center && v.center.top && v.center.right) {
      // Position by centre point: top/right offsets shifted back by
      // half the button's size so the element's centroid sits exactly
      // at (center.top, center.right) from the top-right of the
      // viewport.  When size is missing, we treat center as the corner
      // offset (no shift).
      if (v.size) {
        btn.style.top = `calc(${v.center.top} - (${v.size}) / 2)`;
        btn.style.right = `calc(${v.center.right} - (${v.size}) / 2)`;
      } else {
        btn.style.top = v.center.top;
        btn.style.right = v.center.right;
      }
    }
  }

  // Re-evaluate the active variant when the viewport crosses the
  // aspect-ratio threshold (or is otherwise resized).  Cheap: only
  // re-applies styles when the variant actually changes.
  const onResize = () => {
    if (!config) return;
    if (getVariant() !== currentVariant) applyVariantStyles();
  };
  if (typeof window !== 'undefined' && typeof window.addEventListener === 'function') {
    window.addEventListener('resize', onResize);
  }

  // Re-entrancy guard: a fast double-tap shouldn't fire onQuit twice.
  let quitting = false;
  btn.addEventListener('click', () => {
    if (quitting) return;
    quitting = true;
    try {
      onQuit();
    } finally {
      // Allow re-entry after a tick so a future show() / quit cycle
      // works normally.  We don't await onQuit -- it may be async and
      // tear down the scene before resolving.
      setTimeout(() => { quitting = false; }, 0);
    }
  });

  parent.appendChild(btn);

  // If a config was supplied at construction, apply it immediately.
  if (config) applyVariantStyles();

  return {
    element: btn,
    show() {
      btn.style.display = '';
    },
    hide() {
      btn.style.display = 'none';
    },
    /**
     * Replace the styling config and re-apply the active variant's
     * settings.  Pass `null` to revert to the CSS baseline (only
     * imageUrl from createQuitButton's option is preserved).  An
     * object missing one variant falls back to the other internally
     * (e.g. supplying only `standard` makes wide viewports also use
     * the standard styling).
     *
     * @param {QuitButtonConfig | null} newConfig
     */
    setConfig(newConfig) {
      config = newConfig || null;
      if (config) applyVariantStyles();
    },
    destroy() {
      if (typeof window !== 'undefined' && typeof window.removeEventListener === 'function') {
        window.removeEventListener('resize', onResize);
      }
      if (btn.parentNode) btn.parentNode.removeChild(btn);
    },
  };
}
